import type { SpinSummary } from '../../../Generated/SpinSummary.ts'
import type { TaxonomyLadder } from '../../../Generated/TaxonomyLadder.ts'
import type { SpinEvidence } from '../../../Generated/SpinEvidence.ts'
import type { SpinBlastRef } from '../../../Generated/SpinBlastRef.ts'
import type { DisplayTaxon } from '../../../Generated/DisplayTaxon.ts'
import type { Taxas } from '../../../Model/ITaxa.ts'
import { BlastSource, TaxaBlastSources } from '../../../Model/Project/BlastSource.ts'

export const RANK_LABELS = ['Domain', 'Kingdom', 'Phylum', 'Class', 'Order', 'Family', 'Genus', 'Species']

const ORDER_RANK = 4
const GENUS_RANK = 6

/**
 * Below this many members the scores say little: with two, any disagreement is a 1:1 split, so one outlier and two
 * groups look alike. The page asks before showing them.
 */
export const SPARSE_MEMBER_THRESHOLD = 3

const isPlaceholder = (v?: string | null) => !v || ['', 'nan', 'none', 'null'].includes(v.trim().toLowerCase())

export const cleanName = (v?: string | null): string | undefined =>
  isPlaceholder(v) ? undefined : v!.replace(/_/g, ' ').replace(/\s+/g, ' ').trim()

/** The headline name: the deepest decided rank of the display taxon. */
export const headline = (display?: DisplayTaxon): { name: string; rank: number } | undefined => {
  if (display?.depth == null) return undefined
  const name = display.ranks?.[display.depth]?.value
  return name ? { name, rank: display.depth } : undefined
}

/**
 * The name whose chart identity colours the badge: the order, else the deepest rank above it. Class would
 * paint ~9 in 10 CO1 SPINs the same (Insecta), anything below order is too fine to recognise.
 */
export const badgeName = (display?: DisplayTaxon): string | undefined => {
  const ranks = display?.ranks ?? []
  for (let r = Math.min(ORDER_RANK, display?.depth ?? -1); r >= 0; r--) {
    const v = ranks[r]?.value
    if (v) return v
  }
  return undefined
}

/** Genus and species are italic by nomenclature convention. */
export const isItalicRank = (rank: number) => rank >= GENUS_RANK

export interface SourceRow {
  source: string
  derived: boolean
  top?: TaxonomyLadder
  alt?: TaxonomyLadder
  evidence?: SpinEvidence
  ref?: SpinBlastRef
}

/** Derived sources first, then the amplicon's usual order, then anything else the SPIN carries. */
export const sourceRows = (summary: SpinSummary): SourceRow[] => {
  const ladders = (summary.taxonomies ?? []).filter((l) => l != null)
  const present = [...new Set(ladders.map((l) => l.source))]
  const usual = TaxaBlastSources[summary.amplicon as Taxas] ?? Object.values(BlastSource)
  const rank = (s: string) =>
    s === BlastSource.Consensus ? -1 : usual.indexOf(s as BlastSource) === -1 ? 99 : usual.indexOf(s as BlastSource)
  return present
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
    .map((source) => ({
      source,
      derived: source === BlastSource.Consensus,
      top: ladders.find((l) => l.source === source && l.hitRank === 0),
      alt: ladders.find((l) => l.source === source && l.hitRank === 1),
      evidence: summary.evidence?.find((e) => e.source === source),
      ref: summary.blastRefs?.find((r) => r.source === source)
    }))
}

/** Where a source's top hit can be looked at. RDP is a classifier and has no record page. */
export const recordLink = (row: SourceRow): { label: string; url: string } | undefined => {
  if (row.source === BlastSource.BOLD && row.evidence?.boldLink) return { label: 'View in BOLD', url: row.evidence.boldLink }
  if (row.source === BlastSource.NCBI && row.ref?.processId) {
    return { label: 'View in NCBI', url: `https://www.ncbi.nlm.nih.gov/nuccore/${encodeURIComponent(row.ref.processId)}` }
  }
  return undefined
}

const NO_COUNTRY = new Set(['unrecoverable', 'unspecified country'])

/** BIN countries: `|`-joined and underscored ("United_Kingdom|Austria"); names may contain commas. */
export const binCountries = (v?: string | null): string[] =>
  [...new Set((v ?? '').split('|').map(cleanName))]
    .filter((x): x is string => !!x && !NO_COUNTRY.has(x.toLowerCase()))
    .sort((a, b) => a.localeCompare(b))

export interface BinSpecies {
  name: string
  records?: number
}

/** BIN species: `|`- or `,`-joined, optionally with record counts ("Acleris_forskaleana:12|None:3"). */
export const binSpecies = (v?: string | null): BinSpecies[] =>
  (v ?? '')
    .split(/[|,]/)
    .flatMap((entry): BinSpecies[] => {
      const [raw, count] = entry.split(':')
      const name = cleanName(raw)
      return name ? [{ name, records: count ? Number(count) : undefined }] : []
    })
    .sort((a, b) => (b.records ?? 0) - (a.records ?? 0) || a.name.localeCompare(b.name))

export interface Conflict {
  rank: string
  candidates: string[]
  resolvedBy?: string
}

/** The first rank where databases split without a majority, and who decided it if anyone did. */
export const conflict = (display?: DisplayTaxon): Conflict | undefined => {
  const r = display?.conflictRank
  if (r == null || !display) return undefined
  const row = display.ranks?.[r]
  return {
    rank: RANK_LABELS[r]!,
    candidates: row?.candidates ?? [],
    resolvedBy: row?.basis === 'PREFERRED' ? display.preferredSource : undefined
  }
}

/** Agreement of the voting databases at one rank, e.g. "2/3 agree"; undefined when no database fills it. */
export const agreementAt = (display: DisplayTaxon | undefined, rank: number): string | undefined => {
  const row = display?.ranks?.[rank]
  if (!row || row.voters === 0) return undefined
  if (row.value) return `${row.agreeing}/${row.voters} agree`
  const filled = row.candidates?.length ?? 0
  if (filled === 0) return 'not resolved'
  return filled === 1 ? 'one database only' : `conflicting (${filled} candidates)`
}

export const formatScore = (v?: number) => (v == null ? '—' : `${Math.round(v * 100)}%`)

/** RDP's usual confidence cut-off; a rank called below it is a guess the page should not present as firm. */
export const RDP_CONFIDENT = 0.8

/** The RDP bootstrap support at [rank], when the row is RDP and the evidence carries it. */
export const rdpSupportAt = (row: SourceRow, rank: number): number | undefined =>
  row.source === 'RDP' ? (row.evidence?.rdpSupports?.[rank] ?? undefined) : undefined

/** "99.2% identity · 100% coverage" for a BLAST source's top hit; undefined for classifiers and Consensus. */
export const hitQuality = (row: SourceRow): string | undefined => {
  const id = row.ref?.hitPercentage
  if (id == null) return undefined
  const cov = row.ref?.queryCoverage
  return cov == null ? `${id.toFixed(1)}% identity` : `${id.toFixed(1)}% identity · ${cov}% coverage`
}

export interface RefId {
  label: string
  value: string
  url?: string
}

const BOLD_BIN = /BOLD:[A-Z0-9]+/

/** The reference identifiers a source row can show: BOLD BIN, NCBI accession and taxon id. */
export const referenceIds = (row: SourceRow): RefId[] => {
  const ids: RefId[] = []
  const bin = row.evidence?.boldLink?.match(BOLD_BIN)?.[0]
  if (row.source === 'BOLD' && bin) ids.push({ label: 'BIN', value: bin, url: row.evidence?.boldLink })
  if (row.source === 'NCBI' && row.ref?.processId) {
    ids.push({
      label: 'Accession',
      value: row.ref.processId,
      url: `https://www.ncbi.nlm.nih.gov/nuccore/${encodeURIComponent(row.ref.processId)}`
    })
  }
  const taxid = row.source === 'NCBI' ? row.ref?.staxids?.split(';')[0]?.trim() : undefined
  if (taxid && taxid !== '0') {
    ids.push({
      label: 'TaxID',
      value: taxid,
      url: `https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=${encodeURIComponent(taxid)}`
    })
  }
  return ids
}

/** The reference release a source was last evaluated against, e.g. "release 2026-09-24", or its evaluation date. */
export const sourceVersion = (row: SourceRow): string | undefined => {
  const ev = row.evidence
  if (!ev) return undefined
  const versions = ev.dbVersionSet ?? {}
  const own = Object.entries(versions).find(([k]) => k.toLowerCase().includes(row.source.toLowerCase()))?.[1]
  if (own) return `release ${own}`
  const date = ev.evaluatedAt?.slice(0, 10)
  return date ? `evaluated ${date}` : undefined
}

export type ScoreTone = 'good' | 'fair' | 'poor'

const EPSILON = 1e-9

const tone = (v: number | undefined, good: number, fair: number): ScoreTone | undefined =>
  v == null ? undefined : v >= good - EPSILON ? 'good' : v >= fair - EPSILON ? 'fair' : 'poor'

/**
 * Unity averages the 8 ranks' modal shares, so one rank split evenly costs 1/16: good allows at most an even split
 * at species (15/16), fair at most from family down, i.e. members still agree on the order (13/16).
 */
export const unityTone = (v?: number): ScoreTone | undefined => tone(v, 15 / 16, 13 / 16)

/** Keep in sync with `spin.metrics.reach-denominators` in the backend's application.yml. */
const REACH_DENOMINATOR: Record<string, number> = { BOLD: 6, NCBI: 7, Silva: 7, Consensus: 7 }
const DEFAULT_REACH_DENOMINATOR = 8

/** Reach in ranks: good when members typically reach genus (one rank short of the source's full depth), fair at family. */
export const reachTone = (v: number | undefined, source?: string): ScoreTone | undefined => {
  const d = (source && REACH_DENOMINATOR[source]) || DEFAULT_REACH_DENOMINATOR
  return tone(v, (d - 1) / d, (d - 2) / d)
}

export const UNITY_WORD: Record<ScoreTone, string> = { good: 'Coherent', fair: 'Mixed', poor: 'Heterogeneous' }
export const REACH_WORD: Record<ScoreTone, string> = { good: 'Deep', fair: 'Partial', poor: 'Shallow' }

export interface HeadlineScore {
  value?: number
  source?: string
  min?: number
  max?: number
  count: number
}

/**
 * One headline value per metric, from the source the headline taxon comes from (so the score describes the
 * identification shown above it), with the spread over all sources so a disagreeing one stays visible.
 */
export const headlineScore = (bySource: Record<string, number> | undefined, preferred?: string): HeadlineScore => {
  const entries = Object.entries(bySource ?? {})
  const values = entries.map(([, v]) => v)
  const pick = entries.find(([k]) => k.toLowerCase() === preferred?.toLowerCase()) ?? entries.sort((a, b) => b[1] - a[1])[0]
  return {
    value: pick?.[1],
    source: pick?.[0],
    min: values.length ? Math.min(...values) : undefined,
    max: values.length ? Math.max(...values) : undefined,
    count: values.length
  }
}
